import cx from 'classnames';
import { forwardRef, useCallback, useRef, useState } from 'react';

export interface RangeSliderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  min: number;
  max: number;
  step?: number;
  minValue: number;
  maxValue: number;
  disabled?: boolean;
  onChange: (minValue: number, maxValue: number) => void;
}

export const RangeSlider = forwardRef<HTMLDivElement, RangeSliderProps>(
  (
    {
      className,
      min,
      max,
      step = 1,
      minValue,
      maxValue,
      disabled = false,
      onChange,
      ...rest
    },
    ref,
  ) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);

    const getValueFromPosition = useCallback(
      (clientX: number): number => {
        if (!trackRef.current) return min;

        const rect = trackRef.current.getBoundingClientRect();

        const percentage = Math.max(
          0,
          Math.min(1, (clientX - rect.left) / rect.width),
        );

        const rawValue = min + percentage * (max - min);

        // Snap to step
        const steppedValue = Math.round(rawValue / step) * step;

        return Math.max(min, Math.min(max, steppedValue));
      },
      [min, max, step],
    );

    const handlePointerDown = useCallback(
      (event: React.PointerEvent, thumb: 'min' | 'max') => {
        if (disabled) return;

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setIsDragging(thumb);
      },
      [disabled],
    );

    const handlePointerMove = useCallback(
      (event: React.PointerEvent) => {
        if (!isDragging || disabled) return;

        const newValue = getValueFromPosition(event.clientX);

        if (isDragging === 'min') {
          const clampedMin = Math.min(newValue, maxValue - step);
          onChange(clampedMin, maxValue);
        } else {
          const clampedMax = Math.max(newValue, minValue + step);
          onChange(minValue, clampedMax);
        }
      },
      [
        isDragging,
        disabled,
        getValueFromPosition,
        minValue,
        maxValue,
        step,
        onChange,
      ],
    );

    const handlePointerUp = useCallback(
      (event: React.PointerEvent) => {
        if (isDragging) {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setIsDragging(null);
        }
      },
      [isDragging],
    );

    const handleTrackClick = useCallback(
      (event: React.MouseEvent) => {
        if (disabled || isDragging) return;

        const newValue = getValueFromPosition(event.clientX);
        const distanceToMin = Math.abs(newValue - minValue);
        const distanceToMax = Math.abs(newValue - maxValue);

        // Move the closer thumb
        if (distanceToMin <= distanceToMax) {
          const clampedMin = Math.min(newValue, maxValue - step);
          onChange(clampedMin, maxValue);
        } else {
          const clampedMax = Math.max(newValue, minValue + step);
          onChange(minValue, clampedMax);
        }
      },
      [
        disabled,
        isDragging,
        getValueFromPosition,
        minValue,
        maxValue,
        step,
        onChange,
      ],
    );

    // Calculate positions as percentages, accounting for thumb width
    // The thumbs need to stay within the track (2.5px padding on each side)
    // Available width for thumb movement = track width - thumb width
    const minPosition = ((minValue - min) / (max - min)) * 100;
    const maxPosition = ((maxValue - min) / (max - min)) * 100;

    return (
      <div
        ref={ref}
        className={cx(
          className,
          'tw-relative',
          'tw-h-[32px]',
          'tw-cursor-pointer',
          disabled && 'tw-cursor-not-allowed tw-opacity-40',
        )}
        {...rest}
      >
        {/* Track */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          data-testid="range-slider-track-region"
          ref={trackRef}
          className={cx(
            'tw-absolute',
            'tw-inset-0',
            'tw-bg-fill-secondary',
            'tw-rounded-full',
            'tw-overflow-hidden',
            'tw-p-[2.5px]',
          )}
          onClick={handleTrackClick}
        >
          {/* Active range */}
          <div
            className={cx(
              'tw-absolute',
              'tw-top-[0px]',
              'tw-bottom-[0px]',
              'tw-bg-metal-50',
              'dark:tw-bg-metal-70',
              'tw-rounded-full',
            )}
            style={{
              left: `calc( (100% - 32px) * ${minPosition / 100})`,
              right: `calc( (100% - 32px) * ${(100 - maxPosition) / 100})`,
            }}
          />
        </div>

        {/* Min thumb */}
        <div
          data-testid="range-slider-min-thumb"
          className={cx(
            'tw-absolute',
            'tw-top-[2.5px]',
            'tw-w-[27px]',
            'tw-h-[27px]',
            'tw-bg-white',
            'tw-rounded-full',
            'tw-shadow-i1',
            'tw-border-2.5',
            'tw-border-metal-50',
            'dark:tw-border-metal-70',
            'tw-cursor-pointer',
            'tw-z-10',
            disabled && 'tw-cursor-not-allowed',
            isDragging === 'min' && 'tw-z-20',
          )}
          style={{
            left: `calc(2.5px + (100% - 32px) * ${minPosition / 100})`,
          }}
          onPointerDown={(e) => handlePointerDown(e, 'min')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />

        {/* Max thumb */}
        <div
          data-testid="range-slider-max-thumb"
          className={cx(
            'tw-absolute',
            'tw-top-[2.5px]',
            'tw-w-[27px]',
            'tw-h-[27px]',
            'tw-bg-white',
            'tw-rounded-full',
            'tw-shadow-i1',
            'tw-border-2.5',
            'tw-border-metal-50',
            'dark:tw-border-metal-70',
            'tw-cursor-pointer',
            'tw-z-10',
            disabled && 'tw-cursor-not-allowed',
            isDragging === 'max' && 'tw-z-20',
          )}
          style={{
            left: `calc(2.5px + (100% - 32px) * ${maxPosition / 100})`,
          }}
          onPointerDown={(e) => handlePointerDown(e, 'max')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
      </div>
    );
  },
);
